import React, { useEffect } from 'react';
import { BookOpenIcon, LightbulbIcon, XIcon } from 'lucide-react';
import { manualContent } from '../../Manualcontent';
import { usePermissions } from '../../hooks/usePermissions';
import { ModulePermissions } from '../../types';
interface HelpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentModule?: string;
}
export function HelpDrawer({
  isOpen,
  onClose,
  currentModule
}: HelpDrawerProps) {
  const {
    canView
  } = usePermissions();
  const visibleSections = manualContent.filter(section => canView(section.permissionKey as keyof ModulePermissions));
  const contextualSection = visibleSections.find(section => section.id === currentModule);
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    // Bloquea el scroll del fondo mientras el manual está abierto. Sin esto,
    // en el celular el gesto se lo lleva la página de atrás y el manual se
    // siente trabado.
    const scrollPrevio = window.scrollY;
    const estilo = document.body.style;
    const anterior = {
      position: estilo.position,
      top: estilo.top,
      width: estilo.width,
      overflow: estilo.overflow
    };
    estilo.position = 'fixed';
    estilo.top = `-${scrollPrevio}px`;
    estilo.width = '100%';
    estilo.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      estilo.position = anterior.position;
      estilo.top = anterior.top;
      estilo.width = anterior.width;
      estilo.overflow = anterior.overflow;
      window.scrollTo(0, scrollPrevio);
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const sections = contextualSection ? [contextualSection, ...visibleSections.filter(section => section.id !== contextualSection.id)] : visibleSections;
  return <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Manual de usuario" data-id="element-210" data-mp="3oheqt3z2hnwy">
      <button type="button" aria-label="Cerrar manual" className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]" onClick={onClose} data-id="element-211" data-mp="1mq4qjs709guu" />
      
      <aside className="absolute inset-y-0 right-0 flex h-full max-h-[100dvh] w-full max-w-md flex-col bg-white shadow-2xl dark:bg-[#111827]" data-id="element-212" data-mp="1cul2tt2lhoh6">
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-5 dark:border-white/10" data-id="element-213" data-mp="25hxlvt9jre5q">
          <div className="flex items-center gap-3" data-id="element-214" data-mp="2ahk0a61430g4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300" data-id="element-215" data-mp="2ag8w453r7qoh">
              <BookOpenIcon className="h-5 w-5" data-id="element-216" data-mp="20ib89ipt3vwe" />
            </div>
            <div data-id="element-217" data-mp="20kp2xqgkr63f">
              <h2 className="font-semibold text-gray-900 dark:text-white" data-id="element-218" data-mp="l2c5yo2veu42">Manual de usuario</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-219" data-mp="p3fl18d3gm48">
                {contextualSection ? `Ayuda contextual: ${contextualSection.title}` : 'Guías disponibles para tu rol'}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:hover:bg-white/10 dark:hover:text-gray-200" aria-label="Cerrar manual" data-id="element-220" data-mp="cka63bg8snxi">
            
            <XIcon className="h-5 w-5" data-id="element-221" data-mp="3pzzu3zihc8yv" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5" data-id="element-222" data-mp="3q1sx94tvwb24">
          {sections.length === 0 ? <p className="rounded-xl border border-dashed border-gray-200 p-5 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400" data-id="element-223" data-mp="8lb7e8l3tnei">
              No hay guías disponibles para los permisos de este usuario.
            </p> : <div className="space-y-4" data-id="element-224" data-mp="378fd7urplp1a">
              {sections.map((section, index) => <section key={section.id} className={`rounded-xl border p-4 ${index === 0 && contextualSection ? 'border-teal-200 bg-teal-50/60 dark:border-teal-500/30 dark:bg-teal-500/10' : 'border-gray-100 dark:border-white/10'}`} data-id="element-225" data-mp="2aiwf53wmug5f" data-mpi={index}>
              
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-226" data-mp="2o9i3370y1ga6">{section.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-300" data-id="element-227" data-mp="2t8uw69ierjl7">{section.summary}</p>
                  {section.steps && section.steps.length > 0 && <ol className="mt-3 space-y-2" data-id="element-228" data-mp="2y9ipj1u40cil">
                      {section.steps.map((step, stepIndex) => <li key={step} className="flex gap-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300" data-id="element-229" data-mp="378dru2u192gm" data-mpi={stepIndex}>
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-600 dark:bg-white/10 dark:text-gray-300" data-id="element-230" data-mp="17vrsrij8dvs3">
                            {stepIndex + 1}
                          </span>
                          <span data-id="element-231" data-mp="10k3qkujzaiu9">{step}</span>
                        </li>)}
                    </ol>}
                  {section.tips?.[0] && <div className="mt-3 flex gap-2 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300" data-id="element-232" data-mp="vlk0chycqkw3">
                      <LightbulbIcon className="mt-0.5 h-4 w-4 shrink-0" data-id="element-233" data-mp="1o9fvhi89w4qr" />
                      <span data-id="element-234" data-mp="1o7bvhbdhvpvr">{section.tips[0]}</span>
                    </div>}
                  {section.notes?.[0] && <p className="mt-3 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600 dark:bg-white/5 dark:text-gray-300" data-id="element-235" data-mp="1eaz2ygtori2l">{section.notes[0]}</p>}
                </section>)}
            </div>}
        </div>
      </aside>
    </div>;
}